/**
 * Utility to fetch and use API configurations dynamically
 * This allows the app to use API keys from the database instead of hardcoded env vars
 */

import api from '../config/api';

let configCache = {};
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get API configuration for a specific service
 * @param {string} serviceName - Name of the service (e.g., 'recaptcha', 'stripe', 'chatgpt')
 * @param {boolean} forceRefresh - Force refresh from server
 * @returns {Promise<Object>} Configuration object
 */
export const getApiConfig = async (serviceName, forceRefresh = false) => {
  const now = Date.now();
  
  // Return cached config if still valid and not forcing refresh
  if (!forceRefresh && configCache[serviceName] && (now - cacheTimestamp) < CACHE_DURATION) {
    return configCache[serviceName];
  }

  try {
    const response = await api.get(`/config/${serviceName}`);
    if (response.data.success && response.data.configuration) {
      const config = response.data.configuration;
      
      // Only cache if active
      if (config.is_active) {
        configCache[serviceName] = config;
        cacheTimestamp = now;
      }
      
      return config;
    }
    return null;
  } catch (error) {
    console.error(`Failed to fetch API config for ${serviceName}:`, error);
    // Return cached config as fallback
    return configCache[serviceName] || null;
  }
};

/**
 * Get reCAPTCHA site key dynamically
 */
export const getRecaptchaSiteKey = async () => {
  const config = await getApiConfig('recaptcha');
  if (config && config.is_active) {
    // Check additional_config first (for new format)
    if (config.additional_config?.site_key) {
      return config.additional_config.site_key;
    }
    // Fallback to api_key field
    return config.api_key || null;
  }
  
  // Fallback to environment variable
  return process.env.REACT_APP_RECAPTCHA_SITE_KEY || null;
};

/**
 * Get Stripe publishable key dynamically
 */
export const getStripePublishableKey = async () => {
  const config = await getApiConfig('stripe');
  if (config && config.is_active) {
    if (config.additional_config?.publishable_key) {
      return config.additional_config.publishable_key;
    }
    return config.api_key || null;
  }
  
  // Fallback to environment variable
  return process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || null;
};

/**
 * Get ChatGPT API key dynamically
 */
export const getChatGPTApiKey = async () => {
  const config = await getApiConfig('chatgpt');
  if (config && config.is_active) {
    return config.api_key || null;
  }
  
  // Fallback to environment variable
  return process.env.REACT_APP_CHATGPT_API_KEY || null;
};

/**
 * Get Resend API key dynamically
 */
export const getResendApiKey = async () => {
  const config = await getApiConfig('resend');
  if (config && config.is_active) {
    return config.api_key || null;
  }
  
  // Fallback to environment variable
  return process.env.REACT_APP_RESEND_API_KEY || null;
};

/**
 * Get Google Analytics tracking ID dynamically
 */
export const getGoogleAnalyticsId = async () => {
  const config = await getApiConfig('google_analytics');
  if (config && config.is_active) {
    return config.api_key || null;
  }
  
  // Fallback to environment variable
  return process.env.REACT_APP_GA_TRACKING_ID || null;
};

/**
 * Get Google Ads conversion ID dynamically
 */
export const getGoogleAdsId = async () => {
  const config = await getApiConfig('google_ads');
  if (config && config.is_active) {
    return config.api_key || null;
  }
  
  // Fallback to environment variable
  return process.env.REACT_APP_GOOGLE_ADS_ID || null;
};

/**
 * Clear configuration cache
 */
export const clearConfigCache = () => {
  configCache = {};
  cacheTimestamp = 0;
};

